import { FC, useEffect, useState } from 'react';
import { ControlWithFormField, FormFieldControlProps, SelectControl, SelectControlProps } from '@app/form_components';
import { useServices } from '@app/hooks';
import { Design, Option } from '@app/types';
import { compareDesignVersions, CompareResult } from '@app/utilities';

function versionToOption({ version }: { version: string; }): Option<string> {
  return {
    label: version,
    value: version
  };
}

type VersionOption = Option<string> & { serialized: Design; };

export type VersionSelectorControlProps = (
  Omit<SelectControlProps<string | null, VersionOption>, 'classNames' | 'options'>
  & {
    branchId: string;
    /** Whether versions should be fetch before the user clicks the component. */
    eagerLoad?: boolean;
    /** Whether the user can clear the version value. */
    isClearable?: boolean;
    /** Shows a dot next to the latest version @default false. */
    showLatestVersionDot?: boolean;
  }
);

export const VersionSelectorControl: FC<VersionSelectorControlProps> = ({
  branchId,
  eagerLoad,
  isClearable,
  onChange,
  showLatestVersionDot,
  value,
  ...selectProps
}) => {
  const {
    avsService,
    loadingService
  } = useServices();
  const [isLoading, setIsLoading] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string>();
  const [options, setOptions] = useState<Option<string>[]>();

  const downloadVersions = async (forceDownload = false) => {
    if (isLoading || (!forceDownload && options)) {
      return;
    }
    setIsLoading(true);
    const versions = await avsService.getVersions(branchId);
    setLatestVersion(versions[0]?.version);
    setOptions(versions.map(versionToOption));
    setIsLoading(false);
  };

  useEffect(() => {
    if (eagerLoad) {
      downloadVersions();
      return;
    }

    (async () => {
      const latest = await avsService.getLatestVersion(branchId);
      setLatestVersion(latest?.version);
    })();
  }, [eagerLoad]);

  useEffect(() => {
    const newLatestVersion = (
      latestVersion
      && value
      && compareDesignVersions(value, latestVersion) === CompareResult.aIsLarger
    );
    if (newLatestVersion) {
      downloadVersions(true);
    }
  }, [value, latestVersion]);

  return (
    <SelectControl
      {...selectProps}
      isClearable={isClearable}
      isLoading={isLoading}
      onChange={async (newVersion, option) => {
        if (!newVersion || !option) {
          onChange(null, null);
          return;
        }

        ++loadingService.loadingCount;
        const serialized = await avsService.getDesign(branchId, newVersion);
        onChange(newVersion, { ...option, serialized });
        --loadingService.loadingCount;
      }}
      onFocus={() => downloadVersions()}
      options={options ?? ((value) ? [versionToOption({ version: value })] : [])}
      styles={{
        singleValue: (_, { data }) => {
          const addDot = (
            showLatestVersionDot
            && latestVersion
            && latestVersion !== data.value
          );
          return (addDot) ? (
            // Add dot if there is a newer version.
            SelectControl.styleAddDot('var(--theme-accent)')
          ) : {};
        }
      }}
      value={value}
    />
  );
};

export type VersionSelectorProps = (
  VersionSelectorControlProps & FormFieldControlProps
);

export const VersionSelector: FC<VersionSelectorProps> = (props) => {
  return (
    <ControlWithFormField control={VersionSelectorControl} {...props} />
  );
};
